import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
	X,
	ChevronRight,
	ChevronDown,
	CheckCircle2,
	Clock,
	RotateCcw,
	AlertTriangle,
	Plus,
	CircleDollarSign,
	Check,
	Pencil,
	CreditCard,
	Zap,
	Crown,
	Hand,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getFixedMenuCoords } from '@/components/ui/AnchoredDropdownMenu';
import { CopyWrapper } from './CopyWrapper';
import { SessionService } from '@/services';
import { toast } from 'react-toastify';

const PAYMENT_STATUS_INFO: Record<
	string,
	{ label: string; badge: string; icon: React.ReactNode }
> = {
	WAITING: {
		label: 'AGUARDANDO',
		badge: 'bg-amber-50 text-amber-700 border-amber-100',
		icon: <Clock size={14} />,
	},
	DONE: {
		label: 'PAGO',
		badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
		icon: <CheckCircle2 size={14} />,
	},
	FAILED: {
		label: 'FALHOU',
		badge: 'bg-rose-50 text-rose-700 border-rose-100',
		icon: <AlertTriangle size={14} />,
	},
	REFUNDED: {
		label: 'REEMBOLSADO',
		badge: 'bg-zinc-100 text-zinc-600 border-zinc-200',
		icon: <RotateCcw size={14} />,
	},
};

// Configuração visual dos Gateways para o Dropdown
const GATEWAY_OPTIONS: Record<
	Sessions.PaymentGateway,
	{ label: string; icon: React.ReactNode; color: string; bg: string }
> = {
	STRIPE: {
		label: 'Stripe',
		icon: <CreditCard size={16} />,
		color: 'text-purple-600',
		bg: 'hover:bg-purple-50',
	},
	ABACATE_PAY: {
		label: 'Abacate Pay',
		icon: <Zap size={16} />,
		color: 'text-emerald-600',
		bg: 'hover:bg-emerald-50',
	},
	CLUB: {
		label: 'Plumaa Club',
		icon: <Crown size={16} />,
		color: 'text-amber-500',
		bg: 'hover:bg-amber-50',
	},
	MANUAL: {
		label: 'Lançamento Manual',
		icon: <Hand size={16} />,
		color: 'text-stone-900',
		bg: 'hover:bg-stone-50',
	},
};

export type Transaction = Sessions.PaymentDetails;

interface TransactionReceiptModalProps {
	isOpen: boolean;
	onClose: () => void;
	transactions: Transaction[];
	patientName: string;
	sessionId: string;
	sessionDate: string;
	onManualGenerate?: (gateway: Sessions.PaymentGateway) => void;
	onUpdateTransaction?: (
		transactionId: string,
		updates: Partial<Transaction>,
		updatedSession?: Sessions.Session,
	) => void;
	isManualLoading: boolean;
	disableManualButton: boolean;
}

const GatewayPortalDropdown = ({
	onSelect,
	isLoading,
	disabledOptions,
}: {
	onSelect: (gateway: Sessions.PaymentGateway) => void;
	isLoading: boolean;
	disabledOptions: Sessions.PaymentGateway[];
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [coords, setCoords] = useState<{
		top: number;
		left: number;
		width?: number;
		transform?: string;
	} | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const availableGateways = (
		Object.keys(GATEWAY_OPTIONS) as Sessions.PaymentGateway[]
	).filter((gw) => !disabledOptions.includes(gw));

	if (availableGateways.length === 0) return null;

	const toggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isOpen) {
			setIsOpen(false);
			setCoords(null);
			return;
		}
		if (triggerRef.current) {
			setCoords(
				getFixedMenuCoords(triggerRef.current, {
					matchAnchorWidth: true,
					gap: 8,
					estimatedHeight: 220,
				}),
			);
			setIsOpen(true);
		}
	};

	useEffect(() => {
		if (isOpen) {
			const handleClose = () => {
				setIsOpen(false);
				setCoords(null);
			};
			document.addEventListener('mousedown', handleClose);
			window.addEventListener('scroll', handleClose, true);
			return () => {
				document.removeEventListener('mousedown', handleClose);
				window.removeEventListener('scroll', handleClose, true);
			};
		}
	}, [isOpen]);

	return (
		<div className="w-full">
			<button
				ref={triggerRef}
				onClick={toggle}
				disabled={isLoading}
				className={cn(
					'flex w-full items-center justify-center gap-2 rounded-full bg-stone-950 py-4 text-xs font-bold uppercase tracking-wider text-white shadow-xl shadow-stone-200 transition-all hover:bg-stone-800',
					isLoading
						? 'cursor-wait opacity-70'
						: 'active:scale-[0.98]',
				)}
			>
				{isLoading ? (
					<div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
				) : (
					<Plus size={18} strokeWidth={3} />
				)}
				Novo Pagamento
				<ChevronDown
					size={16}
					className={cn(
						'ml-1 transition-transform',
						isOpen && 'rotate-180',
					)}
				/>
			</button>

			{isOpen &&
				coords &&
				createPortal(
					<div
						className="fixed z-[99999] overflow-hidden rounded-2xl border border-solid border-zinc-200 bg-white py-2 shadow-2xl"
						style={{
							top: coords.top,
							left: coords.left,
							width: coords.width,
							transform: coords.transform,
						}}
						onMouseDown={(e) => e.stopPropagation()}
					>
						<div className="mb-1 border-b border-stone-50 px-4 py-2">
							<span className="text-[9px] font-black uppercase tracking-widest text-stone-400">
								Selecione o Gateway
							</span>
						</div>
						{availableGateways.map((key) => {
							const item = GATEWAY_OPTIONS[key];
							return (
								<button
									key={key}
									onClick={() => {
										onSelect(key);
										setIsOpen(false);
									}}
									className={cn(
										'flex w-full items-center gap-3 border-none px-4 py-3 text-left text-[11px] font-bold uppercase outline-none transition-colors',
										item.color,
										item.bg,
									)}
								>
									<span className="shrink-0">
										{item.icon}
									</span>
									<span>{item.label}</span>
								</button>
							);
						})}
					</div>,
					document.body,
				)}
		</div>
	);
};

// ... (Componentes EditableAmount, StatusPortalDropdown, RecursiveDataRender, FullDetailsViewer, ReceiptRow permanecem iguais ao enviado anteriormente)

const EditableAmount = ({
	initialValue,
	onSave,
}: {
	initialValue: string;
	onSave: (val: string) => void;
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [value, setValue] = useState(
		parseFloat(initialValue).toFixed(2).replace('.', ','),
	);

	const handleSave = () => {
		const cleanValue = value.replace(/\./g, '').replace(',', '.');
		if (!isNaN(parseFloat(cleanValue))) {
			onSave(cleanValue);
			setIsEditing(false);
		}
	};

	if (isEditing) {
		return (
			<div className="flex items-center gap-1">
				<span className="mr-1 text-[10px] font-black text-stone-400">
					R$
				</span>
				<input
					autoFocus
					type="text"
					value={value}
					onChange={(e) => {
						const newValue = e.target.value;
						const regex = /^[0-9]*[,]?[0-9]*$/;
						if (regex.test(newValue)) {
							setValue(newValue);
						}
					}}
					onKeyDown={(e) => e.key === 'Enter' && handleSave()}
					className="w-20 rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[13px] font-bold text-stone-900 outline-none focus:border-zinc-500"
				/>
				<button
					onClick={handleSave}
					className="rounded p-1 text-emerald-600 transition-colors hover:bg-emerald-50"
				>
					<Check size={14} strokeWidth={3} />
				</button>
				<button
					onClick={() => setIsEditing(false)}
					className="rounded p-1 text-stone-400 transition-colors hover:bg-stone-100"
				>
					<X size={14} strokeWidth={3} />
				</button>
			</div>
		);
	}

	return (
		<button
			onClick={() => setIsEditing(true)}
			className="group -mr-2 flex cursor-pointer items-center gap-2 rounded px-2 py-0.5 transition-colors hover:bg-stone-50"
		>
			<span className="text-[13px] font-bold text-stone-700">
				R$ {parseFloat(initialValue).toFixed(2).replace('.', ',')}
			</span>
			<Pencil
				size={12}
				className="text-stone-300 transition-colors group-hover:text-zinc-500"
			/>
		</button>
	);
};

const StatusPortalDropdown = ({
	currentStatus,
	onSelect,
}: {
	currentStatus: string;
	onSelect: (status: string) => void;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [coords, setCoords] = useState<{
		top: number;
		left: number;
		transform?: string;
	} | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const info =
		PAYMENT_STATUS_INFO[currentStatus] || PAYMENT_STATUS_INFO.WAITING;

	const optionsToRender = Object.entries(PAYMENT_STATUS_INFO).filter(
		([key]) => key !== currentStatus,
	);

	const toggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isOpen) {
			setIsOpen(false);
			setCoords(null);
			return;
		}
		if (triggerRef.current) {
			setCoords(
				getFixedMenuCoords(triggerRef.current, {
					menuWidth: 240,
					gap: 4,
					estimatedHeight: 160,
				}),
			);
			setIsOpen(true);
		}
	};

	useEffect(() => {
		if (isOpen) {
			const handleClose = (e: Event) => {
				if (
					e.target instanceof Node &&
					triggerRef.current?.parentElement
						?.querySelector('.fixed.z-\\[99999\\]')
						?.contains(e.target)
				) {
					return;
				}
				setIsOpen(false);
				setCoords(null);
			};
			document.addEventListener('mousedown', handleClose);
			window.addEventListener('scroll', handleClose, true);
			return () => {
				document.removeEventListener('mousedown', handleClose);
				window.removeEventListener('scroll', handleClose, true);
			};
		}
	}, [isOpen]);

	return (
		<>
			<button
				ref={triggerRef}
				onClick={toggle}
				className={cn(
					'flex shrink-0 cursor-pointer items-center gap-1.5 rounded border border-solid px-2 py-1 text-[10px] font-black uppercase outline-none transition-all',
					info.badge,
				)}
			>
				{info.icon}
				<span>{info.label}</span>
				<ChevronDown
					size={12}
					className={cn(
						'ml-1 transition-transform',
						isOpen && 'rotate-180',
					)}
				/>
			</button>

			{isOpen &&
				coords &&
				createPortal(
					<div
						className="fixed z-[99999] w-60 overflow-hidden rounded-xl border border-solid border-zinc-200 bg-white py-1 shadow-2xl"
						style={{
							top: coords.top,
							left: coords.left,
							transform: coords.transform,
						}}
						onMouseDown={(e) => e.stopPropagation()}
					>
						{optionsToRender.map(([key, item]) => (
							<button
								key={key}
								onClick={() => {
									onSelect(key);
									setIsOpen(false);
								}}
								className="flex w-full items-center gap-3 border-none px-4 py-2.5 text-left text-[10px] font-bold uppercase text-stone-500 outline-none transition-colors hover:bg-zinc-50"
							>
								<span className="shrink-0 opacity-70">
									{item.icon}
								</span>
								<span className="truncate">{item.label}</span>
							</button>
						))}
					</div>,
					document.body,
				)}
		</>
	);
};

const RecursiveDataRender = ({
	data,
	indent = 0,
}: {
	data: any;
	indent?: number;
}) => {
	if (!data || typeof data !== 'object') return null;
	return (
		<div
			className={cn(
				'flex flex-col gap-1',
				indent > 0 && 'ml-1 mt-1 border-l border-stone-100 pl-4',
			)}
		>
			{Object.entries(data).map(([key, value]) => {
				const isNullOrEmptyString =
					value === null ||
					(typeof value === 'string' && value.trim() === '');
				if (Array.isArray(value) && value.length === 0) return null;
				const displayValue = isNullOrEmptyString
					? '(null)'
					: String(value);
				const isObject =
					typeof value === 'object' && !Array.isArray(value);
				if (key === 'metadata' && displayValue === '(null)')
					return null;
				return (
					<div key={key} className="flex flex-col text-left">
						<div className="flex gap-1.5 text-[11px] leading-tight">
							<span className="mt-0.5 shrink-0 text-[9px] font-bold uppercase text-stone-500">
								{key.replace(/_/g, ' ')}:
							</span>
							{!isObject && (
								<span
									className={cn(
										'break-all font-medium',
										isNullOrEmptyString
											? 'italic text-stone-400'
											: 'text-stone-700',
									)}
								>
									{displayValue}
								</span>
							)}
						</div>
						{isObject && (
							<RecursiveDataRender
								data={value}
								indent={indent + 1}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
};

const FullDetailsViewer = ({ tx }: { tx: Transaction }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	return (
		<div className="mt-2 overflow-hidden rounded-lg border border-stone-100 bg-stone-50/30">
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				className="flex w-full items-center gap-2 px-3 py-1.5 outline-none transition-colors hover:bg-stone-100/50"
			>
				{isExpanded ? (
					<ChevronDown
						size={14}
						strokeWidth={3}
						className="text-stone-500"
					/>
				) : (
					<ChevronRight
						size={14}
						strokeWidth={3}
						className="text-stone-400"
					/>
				)}
				<div className="flex min-w-0 items-center gap-1.5">
					<span className="truncate text-[10px] font-black uppercase tracking-tighter text-stone-500">
						Detalhes da Transação
					</span>
				</div>
			</button>
			{isExpanded && (
				<div className="animate-in fade-in slide-in-from-top-1 overflow-x-hidden border-t border-stone-100 px-3 py-3 text-left duration-200">
					<RecursiveDataRender data={tx} />
				</div>
			)}
		</div>
	);
};

const ReceiptRow = ({ label, value, className }: any) => {
	return (
		<div className="flex items-center gap-2 py-1">
			<span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-stone-400">
				{label}
			</span>
			<div className="mt-2 flex-1 border-b border-dotted border-stone-300" />
			<div
				className={cn(
					'shrink-0 text-right text-[13px] font-bold text-stone-700',
					className,
				)}
			>
				<span>{value || '-'}</span>
			</div>
		</div>
	);
};

export const TransactionReceiptModal = ({
	isOpen,
	onClose,
	transactions = [],
	patientName,
	sessionId,
	sessionDate,
	onManualGenerate,
	onUpdateTransaction,
	isManualLoading,
	disableManualButton,
}: TransactionReceiptModalProps) => {
	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : 'auto';
		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [isOpen]);

	const dateToStringOrNull = (
		dateValue: Date | null | string | undefined,
	): string | null => {
		if (!dateValue) return null;
		if (dateValue instanceof Date) return dateValue.toISOString();
		if (typeof dateValue === 'string') return dateValue;
		return null;
	};

	if (!isOpen) return null;

	const handleUpdateTransactionWrapper = async (
		transactionId: string,
		updates: Partial<Transaction>,
	) => {
		const originalTx = transactions.find((tx) => tx.id === transactionId);
		if (!originalTx) return;

		const newStatus = (updates.status ||
			originalTx.status) as Sessions.PaymentStatus;
		const now = new Date().toISOString();

		const baseBody: Sessions.UpdateManualPaymentBody = {
			amount: updates.amount
				? parseFloat(updates.amount.toString())
				: parseFloat(originalTx.amount),
			currency: 'BRL',
			status: newStatus,
			payment_done_at:
				newStatus === 'DONE'
					? now
					: dateToStringOrNull(originalTx.payment_done_at),
			payment_refunded_at:
				newStatus === 'REFUNDED'
					? now
					: dateToStringOrNull(originalTx.payment_refunded_at),
			payment_failed_at:
				newStatus === 'FAILED'
					? now
					: dateToStringOrNull(originalTx.payment_failed_at),
			payment_expired_at:
				newStatus === 'FAILED'
					? now
					: dateToStringOrNull(originalTx.payment_expired_at),
			gateway_message:
				updates.gateway_message || originalTx.gateway_message,
			metadata: (updates.metadata !== undefined
				? updates.metadata
				: originalTx.metadata) as
				| Record<string, unknown>
				| null
				| undefined,
			gateway_mismatch:
				updates.gateway_mismatch ?? originalTx.gateway_mismatch,
			gateway_mismatch_reason:
				updates.gateway_mismatch_reason ||
				originalTx.gateway_mismatch_reason,
			gateway_mismatch_at: dateToStringOrNull(
				updates.gateway_mismatch_at || originalTx.gateway_mismatch_at,
			),
		};

		try {
			const updatedSession = await SessionService.updateManualPayment({
				sessionId,
				body: baseBody,
			});
			onUpdateTransaction?.(transactionId, updates, updatedSession);
			if (updates.status) onClose();
		} catch (error) {
			console.error('Erro ao atualizar pagamento:', error);
			const message =
				(error as any)?.response?.data?.message ||
				(error as any)?.message ||
				'Ocorreu um erro ao atualizar o pagamento.';
			toast.error(message);
		}
	};

	// Lógica de desativação baseada na existência de pagamentos
	const hasManual = transactions.some((tx) => tx.gateway === 'MANUAL');
	const hasAnyPayment = transactions.length > 0;

	const disabledOptions: Sessions.PaymentGateway[] = [];
	if (hasManual) disabledOptions.push('MANUAL');
	if (hasAnyPayment) {
		disabledOptions.push('STRIPE', 'ABACATE_PAY', 'CLUB');
	}

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div
				className="animate-in zoom-in-95 relative flex max-h-[95vh] w-full max-w-[620px] flex-col bg-transparent shadow-2xl duration-200"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					onClick={onClose}
					className="absolute right-4 top-4 z-20 rounded-full p-1.5 text-stone-400 transition-colors hover:bg-stone-100"
				>
					<X size={20} />
				</button>

				<div className="flex flex-col overflow-y-auto rounded-3xl">
					<div className="rounded-t-3xl bg-white px-4 pb-4 pt-10 text-center sm:px-8">
						<div className="w-full text-left">
							<h2 className="mb-1 text-xl font-black uppercase tracking-tight text-stone-900">
								Editar Pagamento
							</h2>
							<div className="flex flex-col gap-1">
								<span className="text-[11px] font-bold uppercase tracking-wide text-stone-400">
									PACIENTE: {patientName}
								</span>
								<div className="flex items-center gap-2">
									<span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-stone-400">
										ID da sessão:
									</span>
									<CopyWrapper
										valueToCopy={sessionId}
										wrapperClassName="min-w-0"
										buttonClassName="right-[-4px]"
									>
										<span className="block truncate rounded px-2 py-0.5 pr-8 text-[11px] font-black">
											#{sessionId}
										</span>
									</CopyWrapper>
								</div>
							</div>
						</div>
					</div>

					{transactions.length > 0 ? (
						transactions.map((tx, index) => {
							const isManual = tx.gateway === 'MANUAL';
							const info =
								PAYMENT_STATUS_INFO[tx.status] ||
								PAYMENT_STATUS_INFO.WAITING;

							return (
								<React.Fragment key={tx.id}>
									<div className="relative h-6 w-full shrink-0 overflow-hidden bg-transparent">
										<div
											className="absolute inset-0 bg-white"
											style={{
												maskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
												WebkitMaskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
												maskPosition: 'top center',
												maskSize: '100% 100%',
												maskRepeat: 'no-repeat',
											}}
										/>
										<div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-stone-200" />
									</div>

									<div className="bg-white px-4 py-4 sm:px-8">
										<div className="mb-3 flex items-center justify-between">
											<div className="flex items-center gap-2">
												{(() => {
													const isBenefit =
														!!tx.payment_benefit_application;
													return (
														<>
															<div
																className={cn(
																	'h-2 w-2 shrink-0 rounded-full',
																	isBenefit
																		? 'bg-emerald-400'
																		: tx.gateway ===
																		    'STRIPE'
																		  ? 'bg-purple-400'
																		  : isManual
																		    ? 'bg-stone-800'
																		    : 'bg-cyan-400',
																)}
															/>
															<h3 className="truncate text-xs font-black uppercase tracking-widest text-stone-800">
																{isBenefit
																	? 'BENEFÍCIO'
																	: tx.gateway}{' '}
																<span className="text-stone-400">
																	(
																	{String(
																		index +
																			1,
																	).padStart(
																		2,
																		'0',
																	)}
																	)
																</span>
															</h3>
														</>
													);
												})()}
											</div>
										</div>
										<div className="space-y-2">
											<ReceiptRow
												label="Data"
												value={new Date(
													sessionDate,
												).toLocaleDateString('pt-BR')}
											/>
											<ReceiptRow
												label="Hora"
												value={new Date(
													tx.created_at,
												).toLocaleTimeString('pt-BR', {
													hour: '2-digit',
													minute: '2-digit',
												})}
											/>

											<div className="flex items-center gap-2 py-1">
												<span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-stone-400">
													Valor
												</span>
												<div className="mt-2 flex-1 border-b border-dotted border-stone-300" />
												<div className="shrink-0 text-right">
													{isManual ? (
														<EditableAmount
															initialValue={
																tx.amount
															}
															onSave={(newVal) =>
																handleUpdateTransactionWrapper(
																	tx.id,
																	{
																		amount: newVal,
																	},
																)
															}
														/>
													) : (
														<span className="text-[13px] font-bold text-stone-700">
															R${' '}
															{parseFloat(
																tx.amount,
															)
																.toFixed(2)
																.replace(
																	'.',
																	',',
																)}
														</span>
													)}
												</div>
											</div>

											<div className="flex items-center justify-between gap-2 py-1">
												<span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-stone-400">
													LINK DE PAGAMENTO:
												</span>
												<div className="mt-2 flex-1 border-b border-dotted border-stone-300" />
												{tx.checkout_url ? (
													<CopyWrapper
														valueToCopy={
															tx.checkout_url
														}
														wrapperClassName="min-w-0"
														buttonClassName="right-[-4px]"
													>
														<span className="block truncate rounded px-2 py-0.5 pr-8 text-[11px] font-black ">
															{tx.checkout_url}
														</span>
													</CopyWrapper>
												) : (
													<span className="px-2 py-0.5 text-[11px] font-black uppercase text-stone-300">
														Não disponível
													</span>
												)}
											</div>

											<div className="flex items-center justify-between gap-2 py-1">
												<span className="mb-0.5 shrink-0 text-[10px] font-bold uppercase tracking-wider text-stone-400">
													Status do Pagamento
												</span>
												<div className="mt-2 flex-1 border-b border-dotted border-stone-300" />
												{isManual ? (
													<StatusPortalDropdown
														currentStatus={
															tx.status
														}
														onSelect={(v) =>
															handleUpdateTransactionWrapper(
																tx.id,
																{
																	status: v as Sessions.PaymentStatus,
																},
															)
														}
													/>
												) : (
													<div
														className={cn(
															'flex shrink-0 items-center gap-1.5 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-wide',
															info.badge,
														)}
													>
														{info.icon} {info.label}
													</div>
												)}
											</div>
											<FullDetailsViewer tx={tx} />
										</div>
									</div>
								</React.Fragment>
							);
						})
					) : (
						<>
							<div className="relative h-6 w-full shrink-0 overflow-hidden bg-transparent">
								<div
									className="absolute inset-0 bg-white"
									style={{
										maskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
										WebkitMaskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
										maskPosition: 'top center',
										maskSize: '100% 100%',
										maskRepeat: 'no-repeat',
									}}
								/>
								<div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-stone-200" />
							</div>
							<div className="flex flex-col items-center gap-3 bg-white px-4 py-12 text-center sm:px-8">
								<div className="rounded-full bg-stone-50 p-4">
									<CircleDollarSign
										size={32}
										className="text-stone-300"
									/>
								</div>
								<p className="text-sm font-bold uppercase tracking-tight text-stone-500">
									Nenhum pagamento gerado
								</p>
								<p className="max-w-[240px] text-[11px] uppercase leading-tight tracking-widest text-stone-400">
									Ainda não há registros de cobrança para esta
									consulta.
								</p>
							</div>
						</>
					)}

					{/* RODAPÉ DO RECIBO COM DROPDOWN DE GATEWAYS */}
					{!disableManualButton ? (
						<>
							<div className="relative h-6 w-full shrink-0 overflow-hidden bg-transparent">
								<div
									className="absolute inset-0 bg-white"
									style={{
										maskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
										WebkitMaskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
										maskPosition: 'top center',
										maskSize: '100% 100%',
										maskRepeat: 'no-repeat',
									}}
								/>
								<div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-stone-200" />
							</div>
							<div className="rounded-b-3xl bg-white px-4 pb-10 pt-4 text-center sm:px-8">
								<GatewayPortalDropdown
									isLoading={isManualLoading}
									onSelect={(gw) => onManualGenerate?.(gw)}
									disabledOptions={disabledOptions}
								/>
							</div>
						</>
					) : (
						<>
							<div className="relative h-6 w-full shrink-0 overflow-hidden bg-transparent">
								<div
									className="absolute inset-0 bg-white"
									style={{
										maskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
										WebkitMaskImage: `radial-gradient(circle at 0px center, transparent 10px, black 10.5px), radial-gradient(circle at 100% center, transparent 10px, black 10.5px)`,
										maskPosition: 'top center',
										maskSize: '100% 100%',
										maskRepeat: 'no-repeat',
									}}
								/>
								<div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-stone-200" />
							</div>
							<div className="h-8 rounded-b-3xl bg-white" />
						</>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
